import React, { useCallback, useEffect, useMemo } from 'react';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import PropTypes from 'prop-types';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { getConfirmationModalConfig, resolveApiErrorMessage, transformTableData } from 'utils';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import { useAsyncFn } from 'react-use';
import { actions as modalActions } from 'redux/modal';
import { SendFileForm } from 'components/CommonForms';
import { ROLES } from 'appConstants';
import {
  deleteTrainingSession,
  downloadTrainingSessionsImportExample,
  exportTrainingSessions,
  getTrainingSessionsList,
  importTrainingSessions,
} from 'http/trainingSessions';
import getTableConfig from './helpers/getTableConfig';

const TRAINING_SESSION_STATUSES = ['draft', 'scheduled', 'active', 'completed', 'cancelled'];
const TRAINING_PART_METHODS = ['theory', 'practical', 'ojt'];
const pageAlias = 'TrainingSessionsList';

const TrainingSessions = ({ tableData }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { stations } = useSelector(state => state.stations);
  const role = useSelector(state => state.auth?.profile?.role || state.auth?.user?.role);
  const tableConfig = useMemo(() => getTableConfig(), []);
  const tableRows = useMemo(
    () =>
      Array.isArray(tableData.data)
        ? tableData.data.map(item => ({
            ...item,
            statusCode: item.status,
            scopeCode: item.scope,
          }))
        : [],
    [tableData.data],
  );
  const [columns, items] = transformTableData(tableConfig, tableRows);
  const isLoading = tableData.isFetchingTableData;

  const closeModal = useCallback(() => dispatch(modalActions.closeModal()), [dispatch]);

  const showErrorModal = useCallback(
    message => {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message: resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, t],
  );

  const [deleteState, removeSession] = useAsyncFn(
    async id => {
      const result = await deleteTrainingSession(id);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        closeModal();
        tableData.handleForceUpdate();
        return result;
      }
      showErrorModal(result.message);
      return result;
    },
    [closeModal, showErrorModal, tableData],
  );

  const [importState, importSessionsFile] = useAsyncFn(
    async formData => {
      const result = await importTrainingSessions(formData);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        closeModal();
        tableData.handleForceUpdate();
        return result;
      }
      showErrorModal(result.message || result);
      return result;
    },
    [closeModal, showErrorModal, tableData],
  );

  const [, exportSessionsFile] = useAsyncFn(async () => {
    await exportTrainingSessions();
  }, []);

  const [, downloadExampleFile] = useAsyncFn(async () => {
    await downloadTrainingSessionsImportExample();
  }, []);

  const openEditPage = useCallback(
    sessionId => {
      history.push(`${routesAliases.editTrainingSession}/${sessionId}`);
    },
    [history],
  );

  const handleDeleteSession = useCallback(
    sessionId => {
      const [config, element] = getConfirmationModalConfig({
        title: t('trainingSessions.deleteModalTitle'),
        confirmAction: () => removeSession(sessionId),
        rejectAction: closeModal,
        message: t('trainingSessions.deleteConfirmationMessage'),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, removeSession, t],
  );

  const handleImportSessions = useCallback(() => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingSessions.importModalTitle'),
        },
        <SendFileForm
          onSubmitForm={values => importSessionsFile({ data: values.file })}
          formType="import"
          formName="trainingSessions"
          rejectAction={closeModal}
          submitting={importState.loading}
          fileFormat=".xlsx, .csv"
        />,
      ),
    );
  }, [closeModal, dispatch, importSessionsFile, importState.loading, t]);

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'edit',
        getIcon: rowData => (role === ROLES.ADMIN && rowData.scopeCode === 'global' ? 'visibility' : 'edit'),
        getTitle: rowData =>
          role === ROLES.ADMIN && rowData.scopeCode === 'global' ? t('buttons.view') : t('buttons.edit'),
        onClick: event => {
          openEditPage(Number(event.currentTarget.dataset.key));
        },
      },
      {
        type: 'icon',
        icon: 'delete',
        color: 'error',
        onClick: event => {
          handleDeleteSession(Number(event.currentTarget.dataset.key));
        },
        getHidden: rowData =>
          rowData.statusCode !== 'draft' || (role === ROLES.ADMIN && rowData.scopeCode === 'global'),
      },
    ],
    [handleDeleteSession, openEditPage, role, t],
  );

  useEffect(() => {
    tableData.handleChangeFetchMethod(pageAlias, getTrainingSessionsList);
  }, []);

  useEffect(() => {
    if (location.pathname === routesAliases.trainingSessionsList) {
      tableData.handleForceUpdate();
    }
  }, [location.pathname]);

  const stationFilterOptions = useMemo(
    () =>
      (stations || []).map(station => ({
        value: station.id,
        label: station.name,
      })),
    [stations],
  );

  const filters = useMemo(
    () => [
      {
        type: 'search',
        name: 'search',
        label: t('trainingSessions.searchLabel'),
        placeholder: t('trainingSessions.searchPlaceholder'),
      },
      {
        type: 'singleSelect',
        name: 'trainingMethod',
        label: t('trainingSessions.trainingPart'),
        options: TRAINING_PART_METHODS.map(value => ({
          value,
          label: t(`trainingModules.tabs.${value}`),
        })),
      },
      {
        type: 'singleSelect',
        name: 'stationId',
        label: t('tablesLabels.station'),
        options: stationFilterOptions,
      },
      {
        type: 'singleSelect',
        name: 'status',
        label: t('tablesLabels.status'),
        options: TRAINING_SESSION_STATUSES.map(value => ({
          value,
          label: t(`trainingSessions.statuses.${value}`),
        })),
      },
    ],
    [stationFilterOptions, t],
  );

  const actionSectionRight = useMemo(() => {
    const actions = [
      {
        type: 'button',
        icon: 'add',
        title: t('trainingSessions.actionAdd'),
        onClick: () => history.push(routesAliases.createTrainingSession),
      },
      {
        type: 'button',
        icon: 'cloud_download',
        title: t('commonActions.actionExport'),
        onClick: () => exportSessionsFile(),
      },
    ];

    if (role === ROLES.SUPER) {
      actions.push(
        {
          type: 'button',
          icon: 'import_export',
          title: t('commonActions.actionImport'),
          onClick: handleImportSessions,
        },
        {
          type: 'button',
          icon: 'get_app',
          title: t('commonActions.actionDownloadExample'),
          onClick: () => downloadExampleFile(),
        },
      );
    }

    return actions;
  }, [downloadExampleFile, exportSessionsFile, handleImportSessions, history, role, t]);

  return (
    <PageLayout pageTitle={t('trainingSessions.pageTitle')} actionSectionRight={actionSectionRight}>
      <TableFilterToolbar
        filters={filters}
        filtering={tableData.filtering}
        onFilterChange={tableData.handleFiltering}
        onRemoveFilter={tableData.handleRemoveFilter}
        onClearFilters={tableData.handleClearFilters}
      />
      <ModalBusyContainer busy={isLoading || deleteState.loading || importState.loading}>
        <AppTable
          columns={columns}
          data={items}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
          actions={tableActions}
          actionsColumnWidth="120px"
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};

TrainingSessions.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable({
  defaultSorting: { field: 'id', dir: 'desc' },
  allowedFilters: ['search', 'trainingMethod', 'stationId', 'status'],
  fetchingData: 'items',
  sortingFieldsMapping: {
    status: 'statusSort',
  },
  pageAlias,
})(TrainingSessions);
